import { Box, type BoxProps, type ConditionalValue } from '@chakra-ui/react'
import { X } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useModalState } from '~/atoms/ModalState'
import {
  MangoModalSizes,
  useMangoModalStyles,
} from '~/components/shared/componentStyles/MangoModal.style'
import { ScrollArea } from '~/components/shared/ScrollArea'
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '~/components/ui/dialog'
import { Size } from '~/types/ThemeTypes'

import { preventEscapeFromNestedPopup } from '@mangovoice/mango-ui'

import { QuickActionButtonVariants } from './componentStyles/QuickActionButton.style'
import { QuickActionButton } from './QuickActionButton'

/**
 * @property {string | React.ReactNode} header - The header content of the modal
 * @property {string | React.ReactNode} body - The body of the modal - can be a string or a React node - alternatively set as children if supplied.
 * @property {string | React.ReactNode} footer - The footer content of the modal
 * @property {React.ReactNode} children - The body of the modal - can be a string or a React node - alternatively set as body if supplied.  Given priority over body if both are supplied.
 * @property {string} ariaLabel - Accessible name for dialogs without a string `header`.
 * @property {'dialog' | 'alertdialog'} role - Use `alertdialog` for destructive confirmations.
 * @property {() => HTMLElement | null} initialFocusEl - Element focused on open. Defaults to the first
 * focusable element in the body or footer (never the header Close button).
 * @property {() => HTMLElement | null} finalFocusEl - Element focused on close, for when the trigger is gone.
 */
interface MangoModalProps extends Omit<
  BoxProps,
  'open' | 'onOpenChange' | 'isOpen' | 'onClose' | 'role' | 'scrollBehavior'
> {
  size?: ConditionalValue<MangoModalSizes>
  // V3 props
  open?: boolean
  onOpenChange?: (details: { open: boolean }) => void
  // V2 compatibility props
  isOpen?: boolean
  onClose?: () => void
  // Common props
  closeButton?: boolean
  closeOnInteractOutside?: boolean
  closeOnEscape?: boolean
  header?: string | React.ReactNode
  body?: string | React.ReactNode
  footer?: string | React.ReactNode
  scrollable?: boolean
  headerDisabled?: boolean
  ariaLabel?: string
  /** Space-separated ids that name the dialog when it has no header title. */
  ariaLabelledBy?: string
  role?: 'dialog' | 'alertdialog'
  initialFocusEl?: () => HTMLElement | null
  finalFocusEl?: () => HTMLElement | null
}
export const MangoModal = ({
  size = Size.md,
  open,
  onOpenChange,
  isOpen,
  onClose,
  closeButton = true,
  closeOnInteractOutside,
  closeOnEscape,
  header,
  body,
  footer,
  scrollable,
  children,
  maxWidth,
  ariaLabel,
  ariaLabelledBy,
  role,
  initialFocusEl,
  finalFocusEl,
  ...rest
}: MangoModalProps) => {
  const { setActiveModalRef } = useModalState()
  const { t } = useTranslation()
  const styles = useMangoModalStyles({
    size,
  })

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const headerProps = {
    display: styles.header?.display,
    justifyContent: styles.header?.justifyContent,
    alignItems: styles.header?.alignItems,
  }

  const footerProps = {
    display: styles.footer?.display,
    gap: styles.footer?.gap,
  }

  // Handle backward compatibility
  const isOpenCompat = open ?? isOpen
  const handleOpenChange = React.useCallback(
    (details: { open: boolean }) => {
      if (onOpenChange) {
        onOpenChange(details)
      } else if (onClose && !details.open) {
        onClose()
      }
    },
    [onOpenChange, onClose],
  )

  const handleClose = React.useCallback(() => {
    handleOpenChange({ open: false })
  }, [handleOpenChange])

  const closeQuickAction = (
    <QuickActionButton
      onClick={handleClose}
      actionIcon={X}
      iconProps={{ boxSize: '1rem' }}
      tooltipText={t('voicemail.tooltip.close')}
      size={Size.sm}
      variant={QuickActionButtonVariants.Default_100}
      data-no-autofocus
    />
  )

  const headerContent = useMemo(() => {
    if (!header) return null
    if (typeof header === 'string') {
      return <DialogTitle css={styles.headerText}>{header}</DialogTitle>
    }
    return header
  }, [header])

  const bodyContent = useMemo(() => {
    const innerContent = children ?? body
    const modalBody = <DialogBody css={styles.body}>{innerContent}</DialogBody>
    if (scrollable) {
      return (
        <ScrollArea
          scrollbarOptions={{
            showOnHoverOnly: true,
          }}
        >
          {modalBody}
        </ScrollArea>
      )
    }
    return modalBody
  }, [body, children, scrollable])

  return (
    <DialogRoot
      css={styles.dialogContainer}
      open={isOpenCompat ?? false}
      onOpenChange={handleOpenChange}
      closeOnInteractOutside={closeOnInteractOutside}
      closeOnEscape={closeOnEscape}
      onEscapeKeyDown={preventEscapeFromNestedPopup}
      aria-label={ariaLabel}
      role={role}
      initialFocusEl={initialFocusEl}
      finalFocusEl={finalFocusEl}
      {...rest}
    >
      <DialogContent
        ref={(node) => {
          setActiveModalRef(node)
        }}
        css={maxWidth != null ? { ...styles.dialog, maxWidth } : styles.dialog}
        backdrop={true}
        aria-labelledby={ariaLabelledBy}
      >
        {headerContent ? (
          <DialogHeader css={styles.header} {...headerProps}>
            {headerContent}
            {closeButton && closeQuickAction}
          </DialogHeader>
        ) : (
          closeButton && (
            <Box position="absolute" top={2} insetEnd={2} zIndex={1}>
              {closeQuickAction}
            </Box>
          )
        )}
        {bodyContent}
        {footer && (
          <DialogFooter css={styles.footer} {...footerProps}>
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </DialogRoot>
  )
}
